import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowRight,
  BadgeCheck,
  Building2,
  CheckCircle2,
  ClipboardList,
  Gift,
  HandCoins,
  PackagePlus,
  Recycle,
  Search,
  ShieldCheck,
  ShoppingBag,
  Store,
} from 'lucide-react';
import SEO from '../../components/SEO/SEO';
import { useAuth } from '../../contexts/AuthContext';
import booksService, {
  BookBoard,
  BookCategory,
  BookCondition,
  BookListingPayload,
  BookRequestPayload,
  BookShopPayload,
  DonationPayload,
  ListingType,
  PreferredCondition,
  ShopAvailability,
  ShopProductPayload,
  ShopProductType,
  ShopResponsePayload,
} from '../../services/books.service';

const SITE_URL = 'https://localally.in';

const boardOptions: BookBoard[] = ['AP', 'TELANGANA', 'CBSE', 'ICSE', 'STATE', 'OTHER'];
const conditionOptions: BookCondition[] = ['NEW', 'LIKE_NEW', 'USED', 'OLD'];
const categoryOptions: BookCategory[] = ['TEXTBOOK', 'GUIDE', 'COMPETITIVE_EXAM', 'REFERENCE', 'STORY', 'STATIONERY', 'OTHER'];
const productTypeOptions: ShopProductType[] = [
  'NEW_BOOK',
  'USED_BOOK',
  'GUIDE',
  'COMPETITIVE_EXAM_BOOK',
  'REFERENCE_BOOK',
  'STORY_BOOK',
  'NOTEBOOK',
  'STATIONERY',
  'SCHOOL_BAG',
  'OTHER',
];
const shopAvailabilityOptions: ShopAvailability[] = ['AVAILABLE', 'ARRANGE_IN_1_DAY', 'ARRANGE_IN_2_3_DAYS', 'NOT_AVAILABLE'];

type MessageState = {
  type: 'success' | 'error';
  text: string;
} | null;

const getRecordId = (item: any) => {
  if (!item) return '';
  if (typeof item.id === 'string') return item.id;
  if (typeof item._id === 'string') return item._id;
  if (typeof item._id?.$oid === 'string') return item._id.$oid;
  if (typeof item._id?.toString === 'function') return item._id.toString();
  return '';
};

const formatEnum = (value?: string) =>
  String(value || '')
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const parseNumber = (value: string) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && value.trim() ? parsed : undefined;
};

const pageSchema = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'LocalAlly Books & Stationery',
  serviceType: 'Local books, stationery and book shop discovery',
  provider: {
    '@type': 'Organization',
    name: 'LocalAlly',
    url: SITE_URL,
  },
  description:
    'LocalAlly Books & Stationery connects students, parents, donors and verified local book shops for books and stationery.',
};

const PageShell: React.FC<{
  title: string;
  description: string;
  path: string;
  eyebrow?: string;
  children: React.ReactNode;
}> = ({ title, description, path, eyebrow = 'LocalAlly Books & Stationery', children }) => (
  <div className="min-h-screen bg-slate-50">
    <SEO title={`${title} | LocalAlly`} description={description} path={path} schema={pageSchema} />
    <section className="border-b border-slate-200 bg-white">
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <p className="text-sm font-semibold text-cyan-700">{eyebrow}</p>
        <h1 className="mt-3 max-w-4xl text-4xl font-semibold text-slate-950">{title}</h1>
        <p className="mt-4 max-w-3xl text-base leading-7 text-slate-600">{description}</p>
      </div>
    </section>
    {children}
  </div>
);

const Message: React.FC<{ state: MessageState }> = ({ state }) => {
  if (!state) return null;
  const text = typeof state.text === 'string' ? state.text : String(state.text || '');
  return (
    <div className={`rounded-md border px-4 py-3 text-sm ${state.type === 'success' ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-rose-200 bg-rose-50 text-rose-800'}`}>
      {text}
    </div>
  );
};

const resolveApiErrorMessage = (error: any, fallback: string): string => {
  const payload = error?.response?.data;
  const candidates = [payload?.message, payload?.error, error?.message];

  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim()) return candidate;
    if (Array.isArray(candidate)) {
      const text = candidate.filter((item) => typeof item === 'string' && item.trim()).join(' ');
      if (text) return text;
    }
    if (candidate && typeof candidate === 'object') {
      const nested = candidate.message || candidate.error;
      if (typeof nested === 'string' && nested.trim()) return nested;
    }
  }

  if (payload && typeof payload === 'object') {
    const nested = payload.message || payload.error;
    if (typeof nested === 'string' && nested.trim()) return nested;
  }

  return fallback;
};

const Field: React.FC<{
  label: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  type?: string;
  placeholder?: string;
}> = ({ label, value, onChange, required, type = 'text', placeholder }) => (
  <label className="block text-sm font-medium text-slate-700">
    {label}
    {required && <span className="ml-1 text-rose-600">*</span>}
    <input
      type={type}
      value={value}
      required={required}
      placeholder={placeholder}
      onChange={(event) => onChange(event.target.value)}
      className="mt-2 w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none transition focus:border-cyan-500"
    />
  </label>
);

const SelectField: React.FC<{
  label: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
  required?: boolean;
}> = ({ label, value, options, onChange, required }) => (
  <label className="block text-sm font-medium text-slate-700">
    {label}
    {required && <span className="ml-1 text-rose-600">*</span>}
    <select
      value={value}
      required={required}
      onChange={(event) => onChange(event.target.value)}
      className="mt-2 w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none transition focus:border-cyan-500"
    >
      <option value="">Select</option>
      {options.map((option) => (
        <option key={option} value={option}>
          {formatEnum(option)}
        </option>
      ))}
    </select>
  </label>
);

const TextAreaField: React.FC<{
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}> = ({ label, value, onChange, placeholder }) => (
  <label className="block text-sm font-medium text-slate-700">
    {label}
    <textarea
      value={value}
      placeholder={placeholder}
      onChange={(event) => onChange(event.target.value)}
      rows={4}
      className="mt-2 w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none transition focus:border-cyan-500"
    />
  </label>
);

const PrimaryButton: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement>> = ({ children, className = '', ...props }) => (
  <button
    {...props}
    className={`inline-flex items-center justify-center gap-2 rounded-md bg-slate-950 px-5 py-3 text-sm font-semibold text-white transition hover:bg-cyan-700 disabled:cursor-not-allowed disabled:opacity-60 ${className}`}
  >
    {children}
  </button>
);

const LinkCard: React.FC<{
  to: string;
  title: string;
  description: string;
  icon: React.ElementType;
}> = ({ to, title, description, icon: Icon }) => (
  <Link to={to} className="group rounded-lg border border-slate-200 bg-white p-5 shadow-sm transition hover:border-cyan-300 hover:shadow-md">
    <span className="flex h-10 w-10 items-center justify-center rounded-md bg-cyan-50 text-cyan-700">
      <Icon className="h-5 w-5" aria-hidden="true" />
    </span>
    <h2 className="mt-4 text-lg font-semibold text-slate-950">{title}</h2>
    <p className="mt-2 text-sm leading-6 text-slate-600">{description}</p>
    <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-cyan-700">
      Open <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" aria-hidden="true" />
    </span>
  </Link>
);

const SafetyBand = () => (
  <section className="bg-white">
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-5">
        <div className="flex items-start gap-3">
          <ShieldCheck className="mt-1 h-5 w-5 shrink-0 text-amber-700" aria-hidden="true" />
          <div>
            <h2 className="text-base font-semibold text-amber-950">Safety guidance</h2>
            <p className="mt-2 text-sm leading-6 text-amber-900">
              Meet in a public place, check book condition before payment, do not pay advance to unknown sellers, and report fake listings. Verified shop badges mean LocalAlly has checked basic shop details.
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const BookListingCard: React.FC<{ item: any }> = ({ item }) => (
  <Link to={`/books/listings/${getRecordId(item)}`} className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm transition hover:border-cyan-300">
    <div className="flex items-start justify-between gap-3">
      <div>
        <h3 className="text-lg font-semibold text-slate-950">{item.title}</h3>
        <p className="mt-2 text-sm text-slate-600">{[item.classOrCourse, item.subject, item.board].filter(Boolean).join(' • ') || 'Book listing'}</p>
      </div>
      <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">{formatEnum(item.listingType)}</span>
    </div>
    <p className="mt-4 text-sm leading-6 text-slate-600">{item.description || 'Check details, location, price and condition before contacting the seller.'}</p>
    <div className="mt-4 flex flex-wrap gap-2 text-xs font-semibold">
      <span className="rounded-full bg-cyan-50 px-3 py-1 text-cyan-700">{formatEnum(item.condition)}</span>
      {typeof item.sellingPrice === 'number' && <span className="rounded-full bg-emerald-50 px-3 py-1 text-emerald-700">INR {item.sellingPrice}</span>}
      {item.location?.area && <span className="rounded-full bg-slate-100 px-3 py-1 text-slate-700">{item.location.area}</span>}
    </div>
  </Link>
);

const ShopCard: React.FC<{ shop: any }> = ({ shop }) => (
  <Link to={`/book-shops/${getRecordId(shop)}`} className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm transition hover:border-cyan-300">
    <div className="flex items-start justify-between gap-3">
      <div>
        <h3 className="text-lg font-semibold text-slate-950">{shop.shopName}</h3>
        <p className="mt-1 text-sm text-slate-600">{[shop.area, shop.district, shop.pincode].filter(Boolean).join(', ')}</p>
      </div>
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
        <BadgeCheck className="h-3.5 w-3.5" aria-hidden="true" /> Verified
      </span>
    </div>
    <div className="mt-4 flex flex-wrap gap-2 text-xs font-semibold">
      {shop.offersNewBooks && <span className="rounded-full bg-cyan-50 px-3 py-1 text-cyan-700">New books</span>}
      {shop.offersUsedBooks && <span className="rounded-full bg-violet-50 px-3 py-1 text-violet-700">Used books</span>}
      {shop.offersStationery && <span className="rounded-full bg-amber-50 px-3 py-1 text-amber-700">Stationery</span>}
      {shop.buysUsedBooks && <span className="rounded-full bg-emerald-50 px-3 py-1 text-emerald-700">Buys used books</span>}
    </div>
  </Link>
);

export const BooksLandingPage: React.FC = () => (
  <PageShell
    title="LocalAlly Books & Stationery"
    description="Buy, sell, exchange, donate, request books from nearby shops, and help verified local book shops become digital partners."
    path="/books"
  >
    <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <LinkCard to="/books/search" title="Buy Used Books" description="Search mixed results from students, parents, donations and verified shops." icon={Search} />
        <LinkCard to="/books/sell" title="Sell Books" description="Post used books for admin review and local discovery." icon={ShoppingBag} />
        <LinkCard to="/books/exchange" title="Exchange Books" description="Offer book exchanges with local students and parents." icon={Recycle} />
        <LinkCard to="/books/donate" title="Donate Books" description="Donate books to students, scholarship learners or future NGO/school drives." icon={Gift} />
        <LinkCard to="/book-shops" title="Find Book Shops" description="Discover verified local book and stationery shops." icon={Store} />
        <LinkCard to="/books/request-from-shops" title="Request New Books" description="Send book requests to nearby verified shops." icon={ClipboardList} />
        <LinkCard to="/books/sell-to-shops" title="Sell to Book Shops" description="Let local shops give buy-back offers for used books." icon={HandCoins} />
        <LinkCard to="/book-shop/register" title="Shop Partner" description="Register a book or stationery shop for verification." icon={Building2} />
      </div>
    </section>
    <section className="bg-white">
      <div className="mx-auto grid max-w-7xl gap-4 px-4 py-10 sm:px-6 lg:grid-cols-3 lg:px-8">
        {[
          ['Do not replace shops', 'LocalAlly connects students, parents, donors and trusted local book shops in one education marketplace.'],
          ['Verified shop profiles', 'Shops can list new books, used books, stationery, offers, request handling and buy-back support.'],
          ['MVP safety-first flow', 'No online payment or delivery complexity in this phase. Users verify condition and contact locally.'],
        ].map(([title, text]) => (
          <article key={title} className="rounded-lg border border-slate-200 p-5">
            <h2 className="text-lg font-semibold text-slate-950">{title}</h2>
            <p className="mt-2 text-sm leading-6 text-slate-600">{text}</p>
          </article>
        ))}
      </div>
    </section>
    <SafetyBand />
  </PageShell>
);

export const BooksSearchPage: React.FC = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const loadResults = useCallback(async (searchQuery: string) => {
    setLoading(true);
    try {
      const data = await booksService.search(searchQuery ? { q: searchQuery } : undefined);
      setResults(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadResults('');
  }, [loadResults]);

  return (
    <PageShell
      title="Search Books & Stationery"
      description="Search used books, shop products, donation listings and nearby verified book shops together."
      path="/books/search"
    >
      <section className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <form
          className="flex flex-col gap-3 rounded-lg border border-slate-200 bg-white p-4 sm:flex-row"
          onSubmit={(event) => {
            event.preventDefault();
            void loadResults(query);
          }}
        >
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search book title, class, subject, board or area"
            className="min-h-[44px] flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-cyan-500"
          />
          <PrimaryButton type="submit" disabled={loading}>
            <Search className="h-4 w-4" aria-hidden="true" /> {loading ? 'Searching...' : 'Search'}
          </PrimaryButton>
        </form>
        <div className="mt-8 grid gap-8">
          <ResultSection title="Used, exchange and donation listings" items={results?.listings || []} render={(item) => <BookListingCard key={getRecordId(item)} item={item} />} />
          <ResultSection title="Verified shop products" items={results?.shopProducts || []} render={(item) => (
            <article key={getRecordId(item)} className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
              <h3 className="text-lg font-semibold text-slate-950">{item.title}</h3>
              <p className="mt-2 text-sm text-slate-600">{[formatEnum(item.productType), item.classOrCourse, item.subject].filter(Boolean).join(' • ')}</p>
              <div className="mt-4 flex gap-2 text-xs font-semibold">
                {typeof item.discountPrice === 'number' && <span className="rounded-full bg-emerald-50 px-3 py-1 text-emerald-700">INR {item.discountPrice}</span>}
                <span className="rounded-full bg-slate-100 px-3 py-1 text-slate-700">{formatEnum(item.stockStatus)}</span>
              </div>
            </article>
          )} />
          <ResultSection title="Nearby verified book shops" items={results?.bookShops || []} render={(shop) => <ShopCard key={getRecordId(shop)} shop={shop} />} />
        </div>
      </section>
      <SafetyBand />
    </PageShell>
  );
};

const ResultSection: React.FC<{
  title: string;
  items: any[];
  render: (item: any) => React.ReactNode;
}> = ({ title, items, render }) => (
  <section>
    <div className="flex items-center justify-between gap-4">
      <h2 className="text-2xl font-semibold text-slate-950">{title}</h2>
      <span className="text-sm font-medium text-slate-500">{items.length} found</span>
    </div>
    {items.length ? (
      <div className="mt-4 grid gap-4 md:grid-cols-2 lg:grid-cols-3">{items.map(render)}</div>
    ) : (
      <div className="mt-4 rounded-lg border border-slate-200 bg-white p-5 text-sm text-slate-600">
        No results yet. Try another keyword or use the request-from-shops page.
      </div>
    )}
  </section>
);

const useListingForm = (listingType: ListingType) => {
  const [payload, setPayload] = useState<BookListingPayload>({
    title: '',
    category: 'TEXTBOOK',
    condition: 'USED',
    listingType,
    location: { area: '' },
    photos: [],
  });

  const update = (patch: Partial<BookListingPayload>) => setPayload((current) => ({ ...current, ...patch }));
  const updateLocation = (patch: Partial<NonNullable<BookListingPayload['location']>>) =>
    setPayload((current) => ({ ...current, location: { ...(current.location || { area: '' }), ...patch } }));

  return { payload, update, updateLocation };
};

const createListingPayload = (listingType: ListingType): BookListingPayload => ({
  title: '',
  category: 'TEXTBOOK',
  condition: 'USED',
  listingType,
  location: { area: '' },
});

const cleanBookListingPayload = (item: BookListingPayload): BookListingPayload =>
  Object.fromEntries(
    Object.entries(item).filter(([, value]) => {
      if (value === '' || value === undefined) return false;
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        return Object.values(value).some((nested) => nested !== '' && nested !== undefined);
      }
      return true;
    }),
  ) as BookListingPayload;

const toDonationPayload = (item: BookListingPayload): DonationPayload => ({
  title: item.title,
  classOrCourse: item.classOrCourse,
  board: item.board,
  subject: item.subject,
  condition: item.condition === 'NEW' ? 'LIKE_NEW' : (item.condition as DonationPayload['condition']),
  pickupRequired: false,
  location: item.location,
});

export const BookActionPage: React.FC<{ mode: 'SELL' | 'EXCHANGE' | 'DONATE' | 'SELL_TO_SHOPS' }> = ({ mode }) => {
  const listingType: ListingType = mode === 'EXCHANGE' ? 'EXCHANGE' : mode === 'DONATE' ? 'DONATE' : 'SELL';
  const { payload, update, updateLocation } = useListingForm(listingType);
  const [entryMode, setEntryMode] = useState<'single' | 'bulk'>('single');
  const [bulkRows, setBulkRows] = useState<BookListingPayload[]>([
    createListingPayload(listingType),
    createListingPayload(listingType),
    createListingPayload(listingType),
  ]);
  const [message, setMessage] = useState<MessageState>(null);
  const [submitting, setSubmitting] = useState(false);
  const isDonate = mode === 'DONATE';
  const title = mode === 'SELL_TO_SHOPS' ? 'Sell Used Books to Book Shops' : isDonate ? 'Donate Books' : mode === 'EXCHANGE' ? 'Exchange Books' : 'Sell Used Books';
  const submitLabel = isDonate ? 'Submit donations' : mode === 'EXCHANGE' ? 'Submit exchanges' : 'Submit listings';

  const updateBulkRow = (index: number, patch: Partial<BookListingPayload>) => {
    setBulkRows((current) => current.map((row, rowIndex) => (rowIndex === index ? { ...row, ...patch } : row)));
  };
  const updateBulkLocation = (index: number, patch: Partial<NonNullable<BookListingPayload['location']>>) => {
    setBulkRows((current) =>
      current.map((row, rowIndex) =>
        rowIndex === index ? { ...row, location: { ...(row.location || { area: '' }), ...patch } } : row,
      ),
    );
  };
  const addBulkRow = () => {
    setBulkRows((current) => [...current, createListingPayload(listingType)]);
  };
  const removeBulkRow = (index: number) => {
    setBulkRows((current) => (current.length > 1 ? current.filter((_, rowIndex) => rowIndex !== index) : current));
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setMessage(null);
    try {
      if (entryMode === 'bulk') {
        const validRows = bulkRows.filter((row) => row.title.trim() && row.location?.area?.trim());
        if (!validRows.length) {
          setMessage({ type: 'error', text: 'Add at least one book title and area before submitting.' });
          return;
        }
        if (isDonate) {
          await Promise.all(validRows.map((row) => booksService.createDonation(toDonationPayload(row))));
        } else {
          await Promise.all(validRows.map((row) => booksService.createListing(cleanBookListingPayload(row))));
        }
        setMessage({ type: 'success', text: `${validRows.length} ${validRows.length === 1 ? 'book' : 'books'} submitted successfully. Admin approval is required before public visibility.` });
        setBulkRows([createListingPayload(listingType), createListingPayload(listingType), createListingPayload(listingType)]);
      } else {
        if (isDonate) {
          await booksService.createDonation(toDonationPayload(payload));
        } else {
          await booksService.createListing(cleanBookListingPayload(payload));
        }
        setMessage({ type: 'success', text: 'Submitted successfully. Admin approval is required before it appears publicly.' });
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: error?.response?.data?.message || 'Please sign in and try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageShell title={title} description="Add book details, class or course, board, subject, price, condition and location. Listings go through review before public visibility." path={mode === 'SELL_TO_SHOPS' ? '/books/sell-to-shops' : `/books/${mode.toLowerCase()}`}>
      <section className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8">
        <form onSubmit={submit} className="space-y-5 rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
          <Message state={message} />
          <div className="flex flex-wrap gap-2 rounded-lg bg-slate-100 p-1">
            {[
              ['single', 'Single book'],
              ['bulk', 'Multiple books'],
            ].map(([entryModeValue, label]) => (
              <button
                key={entryModeValue}
                type="button"
                onClick={() => setEntryMode(entryModeValue as 'single' | 'bulk')}
                className={`rounded-md px-4 py-2 text-sm font-semibold transition ${entryMode === entryModeValue ? 'bg-white text-slate-950 shadow-sm' : 'text-slate-600 hover:text-slate-950'}`}
              >
                {label}
              </button>
            ))}
          </div>

          {entryMode === 'single' ? (
            <>
              <Field label="Book title" value={payload.title} required onChange={(value) => update({ title: value })} />
              <div className="grid gap-4 md:grid-cols-2">
                <Field label="Class or course" value={payload.classOrCourse || ''} onChange={(value) => update({ classOrCourse: value })} />
                <SelectField label="Board" value={payload.board || ''} options={boardOptions} onChange={(value) => update({ board: value ? (value as BookBoard) : undefined })} />
                <Field label="Subject" value={payload.subject || ''} onChange={(value) => update({ subject: value })} />
                <SelectField label="Category" value={payload.category} options={categoryOptions} required onChange={(value) => update({ category: value as BookCategory })} />
                <SelectField label="Condition" value={payload.condition} options={conditionOptions} required onChange={(value) => update({ condition: value as BookCondition })} />
                {!isDonate && <Field label={mode === 'SELL_TO_SHOPS' ? 'Expected buy-back price' : 'Selling price'} value={payload.sellingPrice ? String(payload.sellingPrice) : ''} type="number" onChange={(value) => update({ sellingPrice: parseNumber(value) })} />}
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <Field label="Area" value={payload.location?.area || ''} required onChange={(value) => updateLocation({ area: value })} />
                <Field label="Pincode" value={payload.location?.pincode || ''} onChange={(value) => updateLocation({ pincode: value })} />
                <Field label="District" value={payload.location?.district || ''} onChange={(value) => updateLocation({ district: value })} />
                <Field label="State" value={payload.location?.state || ''} onChange={(value) => updateLocation({ state: value })} />
              </div>
              <TextAreaField label="Description" value={payload.description || ''} onChange={(value) => update({ description: value })} placeholder="Mention book condition, edition, missing pages, exchange preference or donation note." />
            </>
          ) : (
            <div className="space-y-4">
              <div className="overflow-x-auto">
                <table className="min-w-[1120px] w-full border-separate border-spacing-y-2 text-sm">
                  <thead>
                    <tr className="text-left text-xs font-semibold uppercase text-slate-500">
                      <th className="px-2">Book title</th>
                      <th className="px-2">Class</th>
                      <th className="px-2">Board</th>
                      <th className="px-2">Subject</th>
                      <th className="px-2">Category</th>
                      <th className="px-2">Condition</th>
                      {!isDonate && <th className="px-2">Price</th>}
                      <th className="px-2">Area</th>
                      <th className="px-2">Pincode</th>
                      <th className="px-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {bulkRows.map((row, index) => (
                      <tr key={index} className="bg-slate-50">
                        <td className="rounded-l-md p-2"><input value={row.title} required onChange={(event) => updateBulkRow(index, { title: event.target.value })} className="w-48 rounded-md border border-slate-300 px-2 py-2" placeholder="Book title" /></td>
                        <td className="p-2"><input value={row.classOrCourse || ''} onChange={(event) => updateBulkRow(index, { classOrCourse: event.target.value })} className="w-28 rounded-md border border-slate-300 px-2 py-2" placeholder="Class" /></td>
                        <td className="p-2">
                          <select value={row.board || ''} onChange={(event) => updateBulkRow(index, { board: event.target.value ? (event.target.value as BookBoard) : undefined })} className="w-32 rounded-md border border-slate-300 bg-white px-2 py-2">
                            <option value="">Board</option>
                            {boardOptions.map((option) => <option key={option} value={option}>{formatEnum(option)}</option>)}
                          </select>
                        </td>
                        <td className="p-2"><input value={row.subject || ''} onChange={(event) => updateBulkRow(index, { subject: event.target.value })} className="w-32 rounded-md border border-slate-300 px-2 py-2" placeholder="Subject" /></td>
                        <td className="p-2">
                          <select value={row.category} onChange={(event) => updateBulkRow(index, { category: event.target.value as BookCategory })} className="w-40 rounded-md border border-slate-300 bg-white px-2 py-2">
                            {categoryOptions.map((option) => <option key={option} value={option}>{formatEnum(option)}</option>)}
                          </select>
                        </td>
                        <td className="p-2">
                          <select value={row.condition} onChange={(event) => updateBulkRow(index, { condition: event.target.value as BookCondition })} className="w-36 rounded-md border border-slate-300 bg-white px-2 py-2">
                            {conditionOptions.map((option) => <option key={option} value={option}>{formatEnum(option)}</option>)}
                          </select>
                        </td>
                        {!isDonate && <td className="p-2"><input type="number" value={row.sellingPrice ? String(row.sellingPrice) : ''} onChange={(event) => updateBulkRow(index, { sellingPrice: parseNumber(event.target.value) })} className="w-24 rounded-md border border-slate-300 px-2 py-2" /></td>}
                        <td className="p-2"><input value={row.location?.area || ''} required onChange={(event) => updateBulkLocation(index, { area: event.target.value })} className="w-32 rounded-md border border-slate-300 px-2 py-2" placeholder="Area" /></td>
                        <td className="p-2"><input value={row.location?.pincode || ''} onChange={(event) => updateBulkLocation(index, { pincode: event.target.value })} className="w-28 rounded-md border border-slate-300 px-2 py-2" placeholder="Pincode" /></td>
                        <td className="rounded-r-md p-2">
                          <button type="button" onClick={() => removeBulkRow(index)} className="rounded-md border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-white">Remove</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button type="button" onClick={addBulkRow} className="rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50">
                Add another book
              </button>
            </div>
          )}
          <PrimaryButton type="submit" disabled={submitting}>
            <PackagePlus className="h-4 w-4" aria-hidden="true" /> {submitting ? 'Submitting...' : entryMode === 'bulk' ? submitLabel : 'Submit for review'}
          </PrimaryButton>
        </form>
      </section>
      <SafetyBand />
    </PageShell>
  );
};

export const RequestFromShopsPage: React.FC = () => {
  const [payload, setPayload] = useState<BookRequestPayload>({ requestedTitle: '', quantity: 1, preferredCondition: 'ANY', location: { area: '' } });
  const [message, setMessage] = useState<MessageState>(null);
  const [submitting, setSubmitting] = useState(false);
  const update = (patch: Partial<BookRequestPayload>) => setPayload((current) => ({ ...current, ...patch }));
  const updateLocation = (patch: Partial<NonNullable<BookRequestPayload['location']>>) =>
    setPayload((current) => ({ ...current, location: { ...(current.location || { area: '' }), ...patch } }));

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setMessage(null);
    try {
      await booksService.createRequest(payload);
      setMessage({ type: 'success', text: 'Book request sent. Verified nearby shops can respond with availability and price.' });
    } catch (error: any) {
      setMessage({ type: 'error', text: error?.response?.data?.message || 'Please sign in and try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageShell title="Request From Nearby Book Shops" description="If a book is not available in used listings, send a request to verified shops near your area." path="/books/request-from-shops">
      <section className="mx-auto max-w-3xl px-4 py-10 sm:px-6 lg:px-8">
        <form onSubmit={submit} className="space-y-5 rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
          <Message state={message} />
          <Field label="Requested book title" value={payload.requestedTitle} required onChange={(value) => update({ requestedTitle: value })} />
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Class or course" value={payload.classOrCourse || ''} onChange={(value) => update({ classOrCourse: value })} />
            <SelectField label="Board" value={payload.board || ''} options={boardOptions} onChange={(value) => update({ board: value as BookBoard })} />
            <Field label="Subject" value={payload.subject || ''} onChange={(value) => update({ subject: value })} />
            <Field label="Exam category" value={payload.examCategory || ''} onChange={(value) => update({ examCategory: value })} />
            <Field label="Quantity" value={String(payload.quantity || 1)} type="number" onChange={(value) => update({ quantity: parseNumber(value) || 1 })} />
            <SelectField label="Preferred condition" value={payload.preferredCondition || 'ANY'} options={['NEW', 'USED', 'ANY']} onChange={(value) => update({ preferredCondition: value as PreferredCondition })} />
            <Field label="Expected price" value={payload.expectedPrice ? String(payload.expectedPrice) : ''} type="number" onChange={(value) => update({ expectedPrice: parseNumber(value) })} />
            <Field label="Area" value={payload.location?.area || ''} required onChange={(value) => updateLocation({ area: value })} />
            <Field label="Pincode" value={payload.location?.pincode || ''} onChange={(value) => updateLocation({ pincode: value })} />
            <Field label="District" value={payload.location?.district || ''} onChange={(value) => updateLocation({ district: value })} />
          </div>
          <PrimaryButton type="submit" disabled={submitting}>
            <ClipboardList className="h-4 w-4" aria-hidden="true" /> {submitting ? 'Sending...' : 'Request from shops'}
          </PrimaryButton>
        </form>
      </section>
    </PageShell>
  );
};

export const BookShopsPage: React.FC = () => {
  const [shops, setShops] = useState<any[]>([]);
  const [query, setQuery] = useState('');

  const loadShops = useCallback(async (searchQuery: string) => {
    const data = await booksService.listBookShops(searchQuery ? { q: searchQuery } : undefined);
    setShops(Array.isArray(data) ? data : []);
  }, []);

  useEffect(() => {
    void loadShops('');
  }, [loadShops]);

  return (
    <PageShell title="Find Book Shops" description="Find verified local book and stationery shops that sell new books, used books and stationery." path="/book-shops">
      <section className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <form className="flex flex-col gap-3 sm:flex-row" onSubmit={(event) => { event.preventDefault(); void loadShops(query); }}>
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search shop, area, district or pincode" className="min-h-[44px] flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-cyan-500" />
          <PrimaryButton type="submit"><Search className="h-4 w-4" /> Search shops</PrimaryButton>
        </form>
        <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {shops.map((shop) => <ShopCard key={getRecordId(shop)} shop={shop} />)}
        </div>
      </section>
    </PageShell>
  );
};

export const BookShopDetailPage: React.FC = () => {
  const { id = '' } = useParams();
  const [shop, setShop] = useState<any>(null);
  const [requestTitle, setRequestTitle] = useState('');
  const [message, setMessage] = useState<MessageState>(null);

  useEffect(() => {
    if (!id) return;
    booksService.getBookShop(id).then(setShop).catch(() => setShop(null));
  }, [id]);

  const sendRequest = async (event: React.FormEvent) => {
    event.preventDefault();
    try {
      await booksService.sendShopEnquiry(id, {
        requestedTitle: requestTitle,
        quantity: 1,
        preferredCondition: 'ANY',
        location: { area: shop?.area || '' },
      });
      setMessage({ type: 'success', text: 'Request sent to this shop.' });
      setRequestTitle('');
    } catch (error: any) {
      setMessage({ type: 'error', text: error?.response?.data?.message || 'Unable to send request.' });
    }
  };

  return (
    <PageShell title={shop?.shopName || 'Book Shop'} description="Verified local book shop details, services and enquiry option." path={`/book-shops/${id}`}>
      <section className="mx-auto grid max-w-7xl gap-6 px-4 py-10 sm:px-6 lg:grid-cols-[1fr_380px] lg:px-8">
        <div className="rounded-lg border border-slate-200 bg-white p-5">
          {shop ? (
            <>
              <h2 className="text-2xl font-semibold text-slate-950">{shop.shopName}</h2>
              <p className="mt-2 text-sm leading-6 text-slate-600">{[shop.address, shop.area, shop.district, shop.state, shop.pincode].filter(Boolean).join(', ')}</p>
              <div className="mt-5 grid gap-3 md:grid-cols-2">
                {[
                  ['Owner', shop.ownerName],
                  ['Phone', shop.phone],
                  ['Boards', (shop.availableBoards || []).join(', ') || 'Ask shop'],
                  ['Classes', (shop.supportedClasses || []).join(', ') || 'Ask shop'],
                  ['Opening time', shop.openingTime || 'Ask shop'],
                  ['Weekly holiday', shop.weeklyHoliday || 'Ask shop'],
                ].map(([label, value]) => (
                  <div key={label} className="rounded-md border border-slate-200 bg-slate-50 p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">{label}</p>
                    <p className="mt-1 text-sm font-medium text-slate-900">{value}</p>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <p className="text-sm text-slate-600">Loading shop details...</p>
          )}
        </div>
        <form onSubmit={sendRequest} className="h-fit space-y-4 rounded-lg border border-slate-200 bg-white p-5">
          <Message state={message} />
          <h2 className="text-lg font-semibold text-slate-950">Request a book</h2>
          <Field label="Book title" value={requestTitle} required onChange={setRequestTitle} />
          <PrimaryButton type="submit" disabled={!shop || !requestTitle.trim()}>
            Send request
          </PrimaryButton>
        </form>
      </section>
    </PageShell>
  );
};

export const BookListingDetailPage: React.FC = () => {
  const { id = '' } = useParams();
  const [listing, setListing] = useState<any>(null);

  useEffect(() => {
    if (!id) return;
    booksService.getListing(id).then(setListing).catch(() => setListing(null));
  }, [id]);

  return (
    <PageShell title={listing?.title || 'Book Listing'} description="Book listing details, condition, price, location and safety guidance." path={`/books/listings/${id}`}>
      <section className="mx-auto max-w-4xl px-4 py-10 sm:px-6 lg:px-8">
        <article className="rounded-lg border border-slate-200 bg-white p-5">
          {listing ? (
            <>
              <div className="flex flex-wrap items-center gap-2 text-xs font-semibold">
                <span className="rounded-full bg-cyan-50 px-3 py-1 text-cyan-700">{formatEnum(listing.listingType)}</span>
                <span className="rounded-full bg-slate-100 px-3 py-1 text-slate-700">{formatEnum(listing.condition)}</span>
                {listing.location?.area && <span className="rounded-full bg-emerald-50 px-3 py-1 text-emerald-700">{listing.location.area}</span>}
              </div>
              <h2 className="mt-5 text-2xl font-semibold text-slate-950">{listing.title}</h2>
              <p className="mt-3 text-sm leading-6 text-slate-600">{listing.description || 'No description added.'}</p>
              <div className="mt-5 grid gap-3 md:grid-cols-2">
                <Info label="Class/course" value={listing.classOrCourse} />
                <Info label="Board" value={listing.board} />
                <Info label="Subject" value={listing.subject} />
                <Info label="Price" value={typeof listing.sellingPrice === 'number' ? `INR ${listing.sellingPrice}` : 'Ask seller'} />
              </div>
            </>
          ) : (
            <p className="text-sm text-slate-600">Loading listing...</p>
          )}
        </article>
      </section>
      <SafetyBand />
    </PageShell>
  );
};

const Info: React.FC<{ label: string; value?: string }> = ({ label, value }) => (
  <div className="rounded-md border border-slate-200 bg-slate-50 p-4">
    <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">{label}</p>
    <p className="mt-1 text-sm font-medium text-slate-900">{value || 'Not specified'}</p>
  </div>
);

export const ShopRegisterPage: React.FC = () => {
  const { user, register, verifyRegistrationOtp, logout } = useAuth();
  const navigate = useNavigate();
  const [payload, setPayload] = useState<BookShopPayload>({
    shopName: '',
    ownerName: '',
    phone: '',
    address: '',
    area: '',
    district: '',
    state: '',
    pincode: '',
    availableBoards: [],
    supportedClasses: [],
    categories: ['New Textbooks', 'Used Textbooks', 'Stationery'],
    offersNewBooks: true,
    offersUsedBooks: false,
    offersStationery: true,
    buysUsedBooks: false,
    acceptsBookRequests: true,
  });
  const [accountPayload, setAccountPayload] = useState({
    firstName: '',
    lastName: '',
    email: '',
    username: '',
    phone: '',
    password: '',
    confirmPassword: '',
  });
  const [registrationIdentifier, setRegistrationIdentifier] = useState('');
  const [otp, setOtp] = useState('');
  const [devOtp, setDevOtp] = useState<string | null>(null);
  const [awaitingOtp, setAwaitingOtp] = useState(false);
  const [message, setMessage] = useState<MessageState>(null);
  const [submitting, setSubmitting] = useState(false);
  const currentUser: any = (user as any)?.user ? (user as any).user : user;
  const needsAccountRegistration = !currentUser;
  const registrationTitle = needsAccountRegistration ? 'Book Shop Seller Registration' : 'Register Another Shop';
  const registrationDescription = needsAccountRegistration
    ? 'Create a seller account and submit your book shop or stationery business profile for LocalAlly admin approval.'
    : 'Add another book shop or stationery branch to your seller account for LocalAlly admin approval.';
  const update = (patch: Partial<BookShopPayload>) => setPayload((current) => ({ ...current, ...patch }));
  const updateAccount = (patch: Partial<typeof accountPayload>) => setAccountPayload((current) => ({ ...current, ...patch }));

  useEffect(() => {
    if (!currentUser) return;
    setPayload((current) => ({
      ...current,
      ownerName: current.ownerName || [currentUser.firstName, currentUser.lastName].filter(Boolean).join(' ').trim(),
      phone: current.phone || currentUser.phone || '',
      email: current.email || currentUser.email || '',
    }));
  }, [currentUser]);

  const submitShopProfile = async () => {
    const shopPayload = Object.fromEntries(
      Object.entries(payload).filter(([, value]) => value !== ''),
    ) as BookShopPayload;
    await booksService.registerBookShop(shopPayload);
    setMessage({
      type: 'success',
      text: 'Seller registration submitted. Admin approval is required before this shop can publish books or stationery products.',
    });
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setMessage(null);
    try {
      if (!needsAccountRegistration) {
        await submitShopProfile();
        return;
      }

      if (awaitingOtp) {
        await verifyRegistrationOtp(registrationIdentifier, otp.trim());
        await submitShopProfile();
        logout();
        navigate('/login', { replace: true });
        return;
      }

      if (accountPayload.password !== accountPayload.confirmPassword) {
        setMessage({ type: 'error', text: 'Account passwords do not match.' });
        return;
      }

      const response = await register({
        firstName: accountPayload.firstName.trim(),
        lastName: accountPayload.lastName.trim(),
        username: accountPayload.username.trim(),
        password: accountPayload.password,
        role: 'SELLER',
        phone: accountPayload.phone.trim(),
        ...(accountPayload.email.trim() ? { email: accountPayload.email.trim() } : {}),
      });
      setRegistrationIdentifier(response?.identifier || accountPayload.phone.trim());
      setDevOtp(response?.otp || null);
      setAwaitingOtp(true);
      setMessage({
        type: 'success',
        text: 'Account OTP sent. Enter the OTP below to verify your login and submit the shop profile for admin approval.',
      });
    } catch (error: any) {
      const errorText = resolveApiErrorMessage(error, 'Unable to submit seller registration.');
      const status = error?.response?.status;
      setMessage({
        type: 'error',
        text: status === 409
          ? `${errorText}. If this seller account already exists, log in and open Seller Dashboard.`
          : errorText,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageShell
      title={registrationTitle}
      description={registrationDescription}
      path="/book-shop/register"
    >
      <section className="mx-auto max-w-4xl px-4 py-10 sm:px-6 lg:px-8">
        <form onSubmit={submit} className="space-y-5 rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
          <Message state={message} />
          <div className="rounded-lg border border-cyan-200 bg-cyan-50 p-4">
            <h2 className="text-base font-semibold text-cyan-950">{needsAccountRegistration ? 'Separate seller onboarding' : 'Add another shop'}</h2>
            <p className="mt-2 text-sm leading-6 text-cyan-900">
              {needsAccountRegistration
                ? 'Seller login uses the same LocalAlly auth system, but shop/business details are stored separately and reviewed by admin before product publishing is enabled.'
                : 'This shop will be linked to your seller account. Each shop is reviewed separately before products and requests are enabled.'}
            </p>
          </div>

          {needsAccountRegistration && (
            <div className="space-y-4 rounded-lg border border-slate-200 bg-slate-50 p-4">
              <div>
                <h2 className="text-lg font-semibold text-slate-950">Seller account</h2>
                <p className="mt-1 text-sm text-slate-600">This creates the login used by the shop owner. Use a phone number with country code, for example +91987654321.</p>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <Field label="First name" value={accountPayload.firstName} required onChange={(value) => updateAccount({ firstName: value })} />
                <Field label="Last name" value={accountPayload.lastName} required onChange={(value) => updateAccount({ lastName: value })} />
                <Field label="Login phone" value={accountPayload.phone} required placeholder="+91XXXXXXXXXX" onChange={(value) => updateAccount({ phone: value })} />
                <Field label="Login email optional" value={accountPayload.email} type="email" onChange={(value) => updateAccount({ email: value })} />
                <Field label="Username" value={accountPayload.username} required onChange={(value) => updateAccount({ username: value })} />
                <Field label="Password" value={accountPayload.password} required type="password" onChange={(value) => updateAccount({ password: value })} />
                <Field label="Confirm password" value={accountPayload.confirmPassword} required type="password" onChange={(value) => updateAccount({ confirmPassword: value })} />
              </div>
              {awaitingOtp && (
                <div className="rounded-lg border border-emerald-200 bg-white p-4">
                  <Field label="OTP" value={otp} required onChange={setOtp} />
                  {devOtp && <p className="mt-2 text-xs font-semibold text-emerald-700">Dev OTP: {devOtp}</p>}
                </div>
              )}
            </div>
          )}

          <div>
            <h2 className="text-lg font-semibold text-slate-950">Shop profile</h2>
            <p className="mt-1 text-sm text-slate-600">These details form the shop profile that admin will approve or reject.</p>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Shop name" value={payload.shopName} required onChange={(value) => update({ shopName: value })} />
            <Field label="Owner name" value={payload.ownerName} required onChange={(value) => update({ ownerName: value })} />
            <Field label="Phone" value={payload.phone} required onChange={(value) => update({ phone: value })} />
            <Field label="Email" value={payload.email || ''} type="email" onChange={(value) => update({ email: value })} />
            <Field label="Address" value={payload.address} required onChange={(value) => update({ address: value })} />
            <Field label="Area" value={payload.area} required onChange={(value) => update({ area: value })} />
            <Field label="District" value={payload.district} required onChange={(value) => update({ district: value })} />
            <Field label="State" value={payload.state} required onChange={(value) => update({ state: value })} />
            <Field label="Pincode" value={payload.pincode} required onChange={(value) => update({ pincode: value })} />
            <Field label="GST number optional" value={payload.gstNumber || ''} onChange={(value) => update({ gstNumber: value })} />
            <Field label="Shop name proof URL optional" value={payload.shopNameProofUrl || ''} onChange={(value) => update({ shopNameProofUrl: value })} />
            <Field label="Shop front photo URL optional" value={payload.shopFrontPhotoUrl || ''} onChange={(value) => update({ shopFrontPhotoUrl: value })} />
          </div>
          <div className="grid gap-3 md:grid-cols-3">
            {[
              ['offersNewBooks', 'New books'],
              ['offersUsedBooks', 'Used books'],
              ['offersStationery', 'Stationery'],
              ['offersHomeDelivery', 'Home delivery'],
              ['buysUsedBooks', 'Buys used books'],
              ['acceptsBookRequests', 'Accepts requests'],
            ].map(([key, label]) => (
              <label key={key} className="flex items-center gap-2 rounded-md border border-slate-200 px-3 py-3 text-sm font-medium text-slate-700">
                <input
                  type="checkbox"
                  checked={Boolean((payload as any)[key])}
                  onChange={(event) => update({ [key]: event.target.checked } as any)}
                />
                {label}
              </label>
            ))}
          </div>
          <PrimaryButton type="submit" disabled={submitting}>
            <Store className="h-4 w-4" /> {submitting ? 'Submitting...' : awaitingOtp ? 'Verify OTP and submit seller profile' : needsAccountRegistration ? 'Submit seller registration' : 'Submit shop for approval'}
          </PrimaryButton>
        </form>
      </section>
    </PageShell>
  );
};

export const MyBookListingsPage: React.FC = () => {
  const [items, setItems] = useState<any[]>([]);
  useEffect(() => {
    booksService.myListings().then((data) => setItems(Array.isArray(data) ? data : [])).catch(() => setItems([]));
  }, []);
  return (
    <PageShell title="My Book Listings" description="Track used book sell, exchange and donation listings submitted by you." path="/my-book-listings">
      <section className="mx-auto max-w-5xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">{items.map((item) => <BookListingCard key={getRecordId(item)} item={item} />)}</div>
      </section>
    </PageShell>
  );
};

export const MyBookRequestsPage: React.FC = () => {
  const [items, setItems] = useState<any[]>([]);
  useEffect(() => {
    booksService.myRequests().then((data) => setItems(Array.isArray(data) ? data : [])).catch(() => setItems([]));
  }, []);
  return (
    <PageShell title="My Book Requests" description="Track requests sent to nearby book shops." path="/my-book-requests">
      <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="grid gap-4">
          {items.map((item) => (
            <article key={getRecordId(item)} className="rounded-lg border border-slate-200 bg-white p-5">
              <h2 className="text-lg font-semibold text-slate-950">{item.requestedTitle}</h2>
              <p className="mt-2 text-sm text-slate-600">{[item.classOrCourse, item.subject, item.location?.area].filter(Boolean).join(', ')}</p>
              <span className="mt-4 inline-flex rounded-full bg-cyan-50 px-3 py-1 text-xs font-semibold text-cyan-700">{formatEnum(item.status)}</span>
            </article>
          ))}
        </div>
      </section>
    </PageShell>
  );
};

type ShopRequestDraft = {
  shopId: string;
  availability: ShopAvailability;
  price: string;
  message: string;
  homeDeliveryAvailable: boolean;
};

const getShopRequestDraft = (item: any, shops: any[]): ShopRequestDraft => {
  const response = item?.sellerResponse || {};
  const targetShopId = item?.targetShop?.id || item?.targetShopId || '';
  const fallbackShopId = targetShopId || getRecordId(shops[0]) || '';
  return {
    shopId: String(response.shopId || fallbackShopId),
    availability: (response.availability || 'AVAILABLE') as ShopAvailability,
    price: typeof response.price === 'number' ? String(response.price) : '',
    message: response.message || '',
    homeDeliveryAvailable: Boolean(response.homeDeliveryAvailable),
  };
};

const formatDateTime = (value?: string) => {
  if (!value) return 'Recently';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'Recently';
  return parsed.toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });
};

const DetailText: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
  <div>
    <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">{label}</p>
    <p className="mt-1 text-sm font-medium text-slate-900">{value || 'Not specified'}</p>
  </div>
);

const ShopRequestInbox: React.FC<{ limit?: number; title?: string }> = ({ limit, title = 'Book requests' }) => {
  const [shops, setShops] = useState<any[]>([]);
  const [items, setItems] = useState<any[]>([]);
  const [drafts, setDrafts] = useState<Record<string, ShopRequestDraft>>({});
  const [message, setMessage] = useState<MessageState>(null);
  const [loading, setLoading] = useState(false);
  const [submittingId, setSubmittingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [shopsData, requestsData] = await Promise.all([
        booksService.myBookShops(),
        booksService.shopRequests(),
      ]);
      const normalizedShops = Array.isArray(shopsData) ? shopsData : [];
      const normalizedRequests = Array.isArray(requestsData) ? requestsData : [];
      setShops(normalizedShops);
      setItems(normalizedRequests);
      setDrafts((current) => {
        const next = { ...current };
        normalizedRequests.forEach((item) => {
          const id = getRecordId(item);
          if (!id || next[id]) return;
          next[id] = getShopRequestDraft(item, normalizedShops);
        });
        return next;
      });
    } catch (error: any) {
      setMessage({ type: 'error', text: resolveApiErrorMessage(error, 'Unable to load book requests.') });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const visibleItems = typeof limit === 'number' ? items.slice(0, limit) : items;
  const updateDraft = (requestId: string, patch: Partial<ShopRequestDraft>) => {
    setDrafts((current) => ({
      ...current,
      [requestId]: {
        ...(current[requestId] || getShopRequestDraft(items.find((item) => getRecordId(item) === requestId), shops)),
        ...patch,
      },
    }));
  };

  const respond = async (item: any) => {
    const requestId = getRecordId(item);
    const draft = drafts[requestId] || getShopRequestDraft(item, shops);
    if (!requestId || !draft.shopId) {
      setMessage({ type: 'error', text: 'Select a shop before sending a response.' });
      return;
    }

    const price = parseNumber(draft.price);
    const payload: ShopResponsePayload = {
      shopId: draft.shopId,
      availability: draft.availability,
      homeDeliveryAvailable: draft.homeDeliveryAvailable,
      ...(price !== undefined ? { price } : {}),
      ...(draft.message.trim() ? { message: draft.message.trim() } : {}),
    };

    setSubmittingId(requestId);
    setMessage(null);
    try {
      await booksService.respondToShopRequest(requestId, payload);
      setMessage({ type: 'success', text: 'Seller response saved for this request.' });
      await load();
    } catch (error: any) {
      setMessage({ type: 'error', text: resolveApiErrorMessage(error, 'Unable to save seller response.') });
    } finally {
      setSubmittingId(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-slate-950">{title}</h2>
          <p className="mt-1 text-sm text-slate-600">{items.length} active {items.length === 1 ? 'request' : 'requests'}</p>
        </div>
        <Link to="/shop/book-requests" className="text-sm font-semibold text-cyan-700 transition hover:text-cyan-800">
          View all
        </Link>
      </div>
      <Message state={message} />
      {loading ? (
        <p className="rounded-lg border border-slate-200 bg-white p-5 text-sm text-slate-600">Loading requests...</p>
      ) : visibleItems.length === 0 ? (
        <p className="rounded-lg border border-slate-200 bg-white p-5 text-sm text-slate-600">No active book requests for your shop right now.</p>
      ) : (
        <div className="space-y-4">
          {visibleItems.map((item) => {
            const requestId = getRecordId(item);
            const draft = drafts[requestId] || getShopRequestDraft(item, shops);
            const targetShopId = item.targetShop?.id || item.targetShopId || '';
            const shopOptions = targetShopId ? shops.filter((shop) => getRecordId(shop) === targetShopId) : shops;
            const requester = item.requester || {};
            const locationText = [item.location?.area, item.location?.district, item.location?.state, item.location?.pincode].filter(Boolean).join(', ');
            const response = item.sellerResponse;

            return (
              <article key={requestId} className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                  <div>
                    <div className="flex flex-wrap gap-2 text-xs font-semibold">
                      <span className="rounded-full bg-cyan-50 px-3 py-1 text-cyan-700">{formatEnum(item.status)}</span>
                      <span className="rounded-full bg-slate-100 px-3 py-1 text-slate-700">{formatDateTime(item.createdAt)}</span>
                      {targetShopId ? <span className="rounded-full bg-emerald-50 px-3 py-1 text-emerald-700">Direct request</span> : null}
                    </div>
                    <h3 className="mt-3 text-xl font-semibold text-slate-950">{item.requestedTitle}</h3>
                    <p className="mt-2 text-sm leading-6 text-slate-600">
                      {[item.classOrCourse, item.board, item.subject, item.examCategory].filter(Boolean).map(formatEnum).join(' • ') || 'General book request'}
                    </p>
                  </div>
                  {response ? (
                    <div className="rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-700">
                      <span className="font-semibold text-slate-950">Last response:</span> {formatEnum(response.availability)}
                    </div>
                  ) : null}
                </div>

                <div className="mt-5 grid gap-4 border-y border-slate-100 py-5 md:grid-cols-2 lg:grid-cols-4">
                  <DetailText label="Requester" value={
                    <>
                      {requester.name || 'Requester'}
                      <span className="mt-1 block text-xs font-normal text-slate-500">{formatEnum(requester.role)}{requester.phone ? ` • ${requester.phone}` : ''}</span>
                    </>
                  } />
                  <DetailText label="Quantity / condition" value={`${item.quantity || 1} • ${formatEnum(item.preferredCondition)}`} />
                  <DetailText label="Expected price" value={typeof item.expectedPrice === 'number' ? `INR ${item.expectedPrice}` : 'Ask requester'} />
                  <DetailText label="Location" value={locationText} />
                </div>

                <form
                  className="mt-5 grid gap-4 lg:grid-cols-[1fr_1fr_150px_1fr_auto]"
                  onSubmit={(event) => {
                    event.preventDefault();
                    void respond(item);
                  }}
                >
                  <label className="block text-sm font-medium text-slate-700">
                    Responding shop
                    <select
                      value={draft.shopId}
                      required
                      onChange={(event) => updateDraft(requestId, { shopId: event.target.value })}
                      className="mt-2 w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none transition focus:border-cyan-500"
                    >
                      <option value="">Select shop</option>
                      {shopOptions.map((shop) => {
                        const shopId = getRecordId(shop);
                        return <option key={shopId} value={shopId}>{shop.shopName || shopId}</option>;
                      })}
                    </select>
                  </label>
                  <SelectField label="Availability" value={draft.availability} options={shopAvailabilityOptions} required onChange={(value) => updateDraft(requestId, { availability: value as ShopAvailability })} />
                  <Field label="Price quote" value={draft.price} type="number" onChange={(value) => updateDraft(requestId, { price: value })} />
                  <TextAreaField label="Seller comment" value={draft.message} placeholder="Edition, pickup timing, condition, or alternate title" onChange={(value) => updateDraft(requestId, { message: value })} />
                  <div className="flex flex-col justify-end gap-3">
                    <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                      <input
                        type="checkbox"
                        checked={draft.homeDeliveryAvailable}
                        onChange={(event) => updateDraft(requestId, { homeDeliveryAvailable: event.target.checked })}
                      />
                      Home delivery
                    </label>
                    <PrimaryButton type="submit" disabled={submittingId === requestId}>
                      {submittingId === requestId ? 'Saving...' : response ? 'Update' : 'Respond'}
                    </PrimaryButton>
                  </div>
                </form>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
};

export const ShopDashboardPage: React.FC = () => {
  const [shops, setShops] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  useEffect(() => {
    booksService.myBookShops().then((data) => setShops(Array.isArray(data) ? data : [])).catch(() => setShops([]));
    booksService.myShopProducts().then((data) => setProducts(Array.isArray(data) ? data : [])).catch(() => setProducts([]));
  }, []);
  return (
    <PageShell title="Seller Dashboard" description="Manage book shop profile, products, requests and buy-back offers." path="/shop/dashboard">
      <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="grid gap-4 md:grid-cols-3">
          <Stat title="My shops" value={shops.length} />
          <Stat title="Products" value={products.length} />
          <Stat title="Verification" value={shops[0]?.verificationStatus || 'PENDING'} />
        </div>
        <div className="mt-8 grid gap-4 md:grid-cols-2 xl:grid-cols-5">
          <LinkCard to="/book-shop/register" title="Register shop" description="Add another shop or branch for admin approval." icon={Building2} />
          <LinkCard to="/shop/products/add" title="Add product" description="List books or stationery for admin approval." icon={PackagePlus} />
          <LinkCard to="/shop/book-requests" title="Book requests" description="Respond to nearby book requests." icon={ClipboardList} />
          <LinkCard to="/shop/buyback-offers" title="Buy-back offers" description="Offer prices for used books." icon={HandCoins} />
          <LinkCard to="/shop/subscription" title="Shop plans" description="Free, verified and premium plan ideas." icon={BadgeCheck} />
        </div>
        <div className="mt-10">
          <ShopRequestInbox limit={3} title="Recent book requests" />
        </div>
      </section>
    </PageShell>
  );
};

const Stat: React.FC<{ title: string; value: string | number }> = ({ title, value }) => (
  <div className="rounded-lg border border-slate-200 bg-white p-5">
    <p className="text-sm font-medium text-slate-500">{title}</p>
    <p className="mt-2 text-2xl font-semibold text-slate-950">{value}</p>
  </div>
);

export const ShopProductAddPage: React.FC = () => {
  const [shops, setShops] = useState<any[]>([]);
  const [payload, setPayload] = useState<ShopProductPayload>({ shopId: '', title: '', productType: 'NEW_BOOK', stockStatus: 'AVAILABLE' });
  const [bulkRows, setBulkRows] = useState<ShopProductPayload[]>([
    { shopId: '', title: '', productType: 'NEW_BOOK', stockStatus: 'AVAILABLE' },
    { shopId: '', title: '', productType: 'NEW_BOOK', stockStatus: 'AVAILABLE' },
    { shopId: '', title: '', productType: 'NEW_BOOK', stockStatus: 'AVAILABLE' },
  ]);
  const [entryMode, setEntryMode] = useState<'single' | 'bulk'>('single');
  const [message, setMessage] = useState<MessageState>(null);
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => {
    booksService.myBookShops().then((data) => setShops(Array.isArray(data) ? data : [])).catch(() => setShops([]));
  }, []);
  useEffect(() => {
    const defaultShopId = getRecordId(shops[0]);
    if (!defaultShopId) return;
    if (!payload.shopId) setPayload((current) => ({ ...current, shopId: defaultShopId }));
    setBulkRows((current) => current.map((row) => (row.shopId ? row : { ...row, shopId: defaultShopId })));
  }, [shops, payload.shopId]);
  const update = (patch: Partial<ShopProductPayload>) => setPayload((current) => ({ ...current, ...patch }));
  const updateBulkRow = (index: number, patch: Partial<ShopProductPayload>) => {
    setBulkRows((current) => current.map((row, rowIndex) => (rowIndex === index ? { ...row, ...patch } : row)));
  };
  const addBulkRow = () => {
    setBulkRows((current) => [
      ...current,
      { shopId: payload.shopId || getRecordId(shops[0]), title: '', productType: 'NEW_BOOK', stockStatus: 'AVAILABLE' },
    ]);
  };
  const removeBulkRow = (index: number) => {
    setBulkRows((current) => (current.length > 1 ? current.filter((_, rowIndex) => rowIndex !== index) : current));
  };
  const cleanProductPayload = (product: ShopProductPayload): ShopProductPayload =>
    Object.fromEntries(Object.entries(product).filter(([, value]) => value !== '' && value !== undefined)) as ShopProductPayload;
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setMessage(null);
    try {
      if (entryMode === 'bulk') {
        const validRows = bulkRows.filter((row) => row.shopId && row.title.trim());
        if (!validRows.length) {
          setMessage({ type: 'error', text: 'Add at least one product title before submitting.' });
          return;
        }
        await Promise.all(validRows.map((row) => booksService.createShopProduct(cleanProductPayload(row))));
        setMessage({ type: 'success', text: `${validRows.length} products submitted for admin approval.` });
        setBulkRows([{ shopId: payload.shopId || getRecordId(shops[0]), title: '', productType: 'NEW_BOOK', stockStatus: 'AVAILABLE' }]);
        return;
      }

      await booksService.createShopProduct(cleanProductPayload(payload));
      setMessage({ type: 'success', text: 'Product submitted for admin approval.' });
      setPayload((current) => ({ ...current, title: '', classOrCourse: '', subject: '', price: undefined, discountPrice: undefined, description: '' }));
    } catch (error: any) {
      setMessage({ type: 'error', text: error?.response?.data?.message || 'Unable to add product.' });
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <PageShell title="Add Shop Product" description="List new books, used books, guides, competitive exam books, notebooks or stationery." path="/shop/products/add">
      <section className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8">
        <form onSubmit={submit} className="space-y-5 rounded-lg border border-slate-200 bg-white p-5">
          <Message state={message} />
          <div className="flex flex-wrap gap-2 rounded-lg bg-slate-100 p-1">
            {[
              ['single', 'Single product'],
              ['bulk', 'Multiple products'],
            ].map(([mode, label]) => (
              <button
                key={mode}
                type="button"
                onClick={() => setEntryMode(mode as 'single' | 'bulk')}
                className={`rounded-md px-4 py-2 text-sm font-semibold transition ${entryMode === mode ? 'bg-white text-slate-950 shadow-sm' : 'text-slate-600 hover:text-slate-950'}`}
              >
                {label}
              </button>
            ))}
          </div>
          {shops.length === 0 && (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              Submit and get approval for a book shop seller profile before adding products.
            </div>
          )}

          {entryMode === 'single' ? (
            <>
              <SelectField label="Shop" value={payload.shopId} options={shops.map((shop) => getRecordId(shop))} required onChange={(value) => update({ shopId: value })} />
              <Field label="Product title" value={payload.title} required onChange={(value) => update({ title: value })} />
              <div className="grid gap-4 md:grid-cols-2">
                <SelectField label="Product type" value={payload.productType} options={productTypeOptions} required onChange={(value) => update({ productType: value as ShopProductType })} />
                <SelectField label="Board" value={payload.board || ''} options={boardOptions} onChange={(value) => update({ board: value ? (value as BookBoard) : undefined })} />
                <Field label="Class/course" value={payload.classOrCourse || ''} onChange={(value) => update({ classOrCourse: value })} />
                <Field label="Subject" value={payload.subject || ''} onChange={(value) => update({ subject: value })} />
                <Field label="Price" value={payload.price ? String(payload.price) : ''} type="number" onChange={(value) => update({ price: parseNumber(value) })} />
                <Field label="Discount price" value={payload.discountPrice ? String(payload.discountPrice) : ''} type="number" onChange={(value) => update({ discountPrice: parseNumber(value) })} />
              </div>
              <TextAreaField label="Description" value={payload.description || ''} onChange={(value) => update({ description: value })} />
            </>
          ) : (
            <div className="space-y-4">
              <div className="overflow-x-auto">
                <table className="min-w-[980px] w-full border-separate border-spacing-y-2 text-sm">
                  <thead>
                    <tr className="text-left text-xs font-semibold uppercase text-slate-500">
                      <th className="px-2">Shop</th>
                      <th className="px-2">Title</th>
                      <th className="px-2">Type</th>
                      <th className="px-2">Board</th>
                      <th className="px-2">Class</th>
                      <th className="px-2">Subject</th>
                      <th className="px-2">Price</th>
                      <th className="px-2">Discount</th>
                      <th className="px-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {bulkRows.map((row, index) => (
                      <tr key={index} className="bg-slate-50">
                        <td className="rounded-l-md p-2">
                          <select value={row.shopId} required onChange={(event) => updateBulkRow(index, { shopId: event.target.value })} className="w-32 rounded-md border border-slate-300 bg-white px-2 py-2">
                            <option value="">Shop</option>
                            {shops.map((shop) => <option key={getRecordId(shop)} value={getRecordId(shop)}>{shop.shopName || getRecordId(shop)}</option>)}
                          </select>
                        </td>
                        <td className="p-2"><input value={row.title} required onChange={(event) => updateBulkRow(index, { title: event.target.value })} className="w-48 rounded-md border border-slate-300 px-2 py-2" placeholder="Book/product title" /></td>
                        <td className="p-2">
                          <select value={row.productType} onChange={(event) => updateBulkRow(index, { productType: event.target.value as ShopProductType })} className="w-40 rounded-md border border-slate-300 bg-white px-2 py-2">
                            {productTypeOptions.map((option) => <option key={option} value={option}>{formatEnum(option)}</option>)}
                          </select>
                        </td>
                        <td className="p-2">
                          <select value={row.board || ''} onChange={(event) => updateBulkRow(index, { board: event.target.value ? (event.target.value as BookBoard) : undefined })} className="w-32 rounded-md border border-slate-300 bg-white px-2 py-2">
                            <option value="">Board</option>
                            {boardOptions.map((option) => <option key={option} value={option}>{formatEnum(option)}</option>)}
                          </select>
                        </td>
                        <td className="p-2"><input value={row.classOrCourse || ''} onChange={(event) => updateBulkRow(index, { classOrCourse: event.target.value })} className="w-28 rounded-md border border-slate-300 px-2 py-2" placeholder="Class" /></td>
                        <td className="p-2"><input value={row.subject || ''} onChange={(event) => updateBulkRow(index, { subject: event.target.value })} className="w-32 rounded-md border border-slate-300 px-2 py-2" placeholder="Subject" /></td>
                        <td className="p-2"><input type="number" value={row.price ? String(row.price) : ''} onChange={(event) => updateBulkRow(index, { price: parseNumber(event.target.value) })} className="w-24 rounded-md border border-slate-300 px-2 py-2" /></td>
                        <td className="p-2"><input type="number" value={row.discountPrice ? String(row.discountPrice) : ''} onChange={(event) => updateBulkRow(index, { discountPrice: parseNumber(event.target.value) })} className="w-24 rounded-md border border-slate-300 px-2 py-2" /></td>
                        <td className="rounded-r-md p-2">
                          <button type="button" onClick={() => removeBulkRow(index)} className="rounded-md border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-white">Remove</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button type="button" onClick={addBulkRow} className="rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50">
                Add another row
              </button>
            </div>
          )}
          <PrimaryButton type="submit" disabled={submitting || shops.length === 0}>
            {submitting ? 'Submitting...' : entryMode === 'bulk' ? 'Submit products' : 'Submit product'}
          </PrimaryButton>
        </form>
      </section>
    </PageShell>
  );
};

export const ShopRequestsPage: React.FC = () => {
  return (
    <PageShell title="Shop Book Requests" description="View nearby book requests that your shop can respond to." path="/shop/book-requests">
      <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <ShopRequestInbox title="All book requests" />
      </section>
    </PageShell>
  );
};

export const ShopBuybackOffersPage: React.FC = () => {
  const [items, setItems] = useState<any[]>([]);
  useEffect(() => {
    booksService.shopBuybackOffers().then((data) => setItems(Array.isArray(data) ? data : [])).catch(() => setItems([]));
  }, []);
  return (
    <PageShell title="Shop Buy-Back Offers" description="Track offers your shop has made for used book listings." path="/shop/buyback-offers">
      <section className="mx-auto max-w-5xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="grid gap-4">
          {items.map((item) => (
            <article key={getRecordId(item)} className="rounded-lg border border-slate-200 bg-white p-5">
              <h2 className="text-lg font-semibold text-slate-950">Offer INR {item.offeredPrice}</h2>
              <p className="mt-2 text-sm text-slate-600">{item.message || 'No message added.'}</p>
              <span className="mt-4 inline-flex rounded-full bg-cyan-50 px-3 py-1 text-xs font-semibold text-cyan-700">{formatEnum(item.offerStatus)}</span>
            </article>
          ))}
        </div>
      </section>
    </PageShell>
  );
};

export const ShopSubscriptionPage: React.FC = () => (
  <PageShell title="Book Shop Subscription Ideas" description="Free, verified and premium plan concepts for local book and stationery shops." path="/shop/subscription">
    <section className="mx-auto grid max-w-7xl gap-4 px-4 py-10 sm:px-6 lg:grid-cols-3 lg:px-8">
      {[
        ['Free Plan', ['Basic shop profile', 'Limited product listings', 'Limited monthly enquiries', 'No featured placement']],
        ['Verified Plan', ['Verified badge', 'More product listings', 'More enquiries', 'Request notifications', 'Basic analytics']],
        ['Premium Plan', ['Top placement in nearby searches', 'Featured banner', 'Unlimited products', 'Priority request visibility', 'Seasonal campaign support']],
      ].map(([title, points]) => (
        <article key={title as string} className="rounded-lg border border-slate-200 bg-white p-5">
          <h2 className="text-xl font-semibold text-slate-950">{title}</h2>
          <div className="mt-4 space-y-3">
            {(points as string[]).map((point) => (
              <div key={point} className="flex items-start gap-2 text-sm text-slate-700">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                {point}
              </div>
            ))}
          </div>
        </article>
      ))}
    </section>
  </PageShell>
);

export const AdminBooksPage: React.FC = () => {
  const [overview, setOverview] = useState<any>(null);
  const [shops, setShops] = useState<any[]>([]);
  const [message, setMessage] = useState<MessageState>(null);
  const pendingShops = useMemo(() => shops.filter((shop) => shop.verificationStatus === 'PENDING'), [shops]);

  const load = async () => {
    const [overviewData, shopsData] = await Promise.all([booksService.adminOverview(), booksService.adminBookShops()]);
    setOverview(overviewData);
    setShops(Array.isArray(shopsData) ? shopsData : []);
  };

  useEffect(() => {
    void load().catch(() => undefined);
  }, []);

  const approve = async (id: string) => {
    await booksService.approveBookShop(id);
    setMessage({ type: 'success', text: 'Book shop approved.' });
    await load();
  };

  const reject = async (id: string) => {
    await booksService.rejectBookShop(id, 'Rejected by admin review.');
    setMessage({ type: 'success', text: 'Book shop rejected.' });
    await load();
  };

  return (
    <PageShell title="Admin Books & Stationery" description="Approve book shops, review marketplace signals and monitor book ecosystem growth." path="/admin/books">
      <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <Message state={message} />
        <div className="mt-4 grid gap-4 md:grid-cols-4">
          {Object.entries(overview || {}).map(([key, value]) => <Stat key={key} title={formatEnum(key)} value={String(value)} />)}
        </div>
        <h2 className="mt-10 text-2xl font-semibold text-slate-950">Pending shop approvals</h2>
        <div className="mt-4 grid gap-4">
          {pendingShops.map((shop) => (
            <article key={getRecordId(shop)} className="rounded-lg border border-slate-200 bg-white p-5">
              <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-slate-950">{shop.shopName}</h3>
                  <p className="mt-2 text-sm text-slate-600">{[shop.ownerName, shop.phone, shop.area, shop.district].filter(Boolean).join(' • ')}</p>
                </div>
                <div className="flex gap-2">
                  <PrimaryButton type="button" onClick={() => void approve(getRecordId(shop))}>Approve</PrimaryButton>
                  <button type="button" onClick={() => void reject(getRecordId(shop))} className="rounded-md border border-rose-200 bg-rose-50 px-4 py-2 text-sm font-semibold text-rose-700">
                    Reject
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>
    </PageShell>
  );
};
